import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { postsApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { useCurrentUser } from '@/features/auth'
import { IdentityBadge, VerificationPanel } from '@/features/identity'
import {
  Composer,
  MediaGrid,
  PostActions,
  PostCard,
  PollCard,
  PostMenu,
  QuoteCard,
  replyPolicyOption,
} from '@/features/posts'
import { usePostReplies } from '@/features/timeline'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useT } from '@/i18n'
import { useFormat } from '@/lib/format'
import { renderContent } from '@/lib/text'
import { Link } from 'react-router-dom'

export function PostDetailPage() {
  const { absoluteTime, compactCount } = useFormat()
  const t = useT()
  const { postId = '' } = useParams()
  const { user } = useCurrentUser()

  const thread = useQuery({
    queryKey: keys.postThread(postId),
    queryFn: () => postsApi.thread(postId),
    enabled: Boolean(postId),
  })

  const replies = usePostReplies(postId)
  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(replies.hasNextPage),
    isFetchingNextPage: replies.isFetchingNextPage,
    fetchNextPage: replies.fetchNextPage,
  })

  if (thread.isPending) {
    return (
      <>
        <PageHeader title={t('detail.title')} back />
        <PostListSkeleton count={3} />
      </>
    )
  }

  if (thread.isError) {
    return (
      <>
        <PageHeader title={t('detail.title')} back />
        <ErrorState message={thread.error.message} onRetry={() => thread.refetch()} />
      </>
    )
  }

  const { ancestors, post } = thread.data
  const replyItems = replies.data?.pages.flatMap((page) => page.items) ?? []
  // 回复设置按线程第一条算（D65）。祖先链是正序的，但根帖可能删了或看不到，
  // 所以只有第一条真的是根帖时才点名；否则说一句不带名字的
  const threadAuthor = !post.reply_to
    ? post.author
    : ancestors[0]?.id === post.root_id
      ? ancestors[0].author
      : null
  const policy = replyPolicyOption(post.reply_policy)
  const canReply = post.viewer?.can_reply !== false

  return (
    <>
      <PageHeader title={t('detail.title')} back />

      {/* 祖先链：用竖线连起来 */}
      {ancestors.map((ancestor) => (
        <PostCard key={ancestor.id} post={ancestor} connector="below" />
      ))}

      {/* 主帖放大显示 */}
      <article className="border-b border-line px-4 py-3">
        <div className="flex gap-3">
          <Avatar
            src={post.author.avatar_url}
            alt={post.author.display_name}
            to={`/${post.author.username}`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <Link
                to={`/${post.author.username}`}
                className="block min-w-0 flex-1 truncate font-bold hover:underline"
              >
                {post.author.display_name}
              </Link>
              <PostMenu post={post} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] text-ink-muted">@{post.author.username}</span>
              <IdentityBadge
                authorKind={post.author_kind}
                attestation={post.attestation}
                author={post.author}
              />
            </div>
          </div>
        </div>

        {post.content && (
          <p className="post-text mt-3 text-xl leading-normal">
            {renderContent(post.content, post.entities)}
          </p>
        )}

        {post.poll && <PollCard post={post} />}
        {post.media.length > 0 && <MediaGrid media={post.media} size="detail" />}
        {post.quote_of && <QuoteCard post={post.quote_of} />}

        <p className="mt-3 text-[15px] text-ink-muted">{absoluteTime(post.created_at)}</p>

        <VerificationPanel postId={post.id} />

        {(post.counts.reposts > 0 || post.counts.likes > 0 || post.counts.quotes > 0) && (
          <div className="mt-3 flex gap-5 border-y border-line py-3 text-[15px]">
            {post.counts.reposts > 0 && (
              <span>
                <b>{compactCount(post.counts.reposts)}</b>{' '}
                <span className="text-ink-muted">{t('detail.reposts')}</span>
              </span>
            )}
            {post.counts.quotes > 0 && (
              <span>
                <b>{compactCount(post.counts.quotes)}</b>{' '}
                <span className="text-ink-muted">{t('detail.quotes')}</span>
              </span>
            )}
            {post.counts.likes > 0 && (
              <span>
                <b>{compactCount(post.counts.likes)}</b>{' '}
                <span className="text-ink-muted">{t('detail.likes')}</span>
              </span>
            )}
          </div>
        )}

        <PostActions post={post} size="detail" />
      </article>

      {post.reply_policy !== 'everyone' && (
        <p className="flex items-center gap-2 border-b border-line px-4 py-3 text-[15px] text-ink-muted">
          <policy.icon className="size-4 shrink-0 text-brand-ink" aria-hidden />
          {!threadAuthor
            ? t('post.cannotReply')
            : post.reply_policy === 'following'
              ? t('replyPolicy.noticeFollowing', { username: threadAuthor.username })
              : t('replyPolicy.noticeMentioned', { username: threadAuthor.username })}
        </p>
      )}

      {user && canReply && (
        <div className="border-b border-line">
          <Composer replyTo={post} placeholder={t('composer.replyPlaceholder')} />
        </div>
      )}

      {replies.isPending && <PostListSkeleton count={2} />}
      {replyItems.map((reply) => (
        <PostCard key={reply.id} post={reply} />
      ))}
      {!replies.isPending && replyItems.length === 0 && (
        <EmptyState title={t('detail.noReplies')} description={t('detail.beFirst')} />
      )}

      <div ref={sentinelRef} aria-hidden className="h-px" />
      {replies.isFetchingNextPage && <CenteredSpinner />}
    </>
  )
}
